import { Id } from "./db/db.server";

export const unsubscribeTokenParameterName = "unsubToken";
export type Token = string;
export type UserToken = string;
export const tokenParamName = "token";

export const ACTION_TOKEN_PARAM_NAME = "actionToken";

export enum EmailAction {
  Unsubscribe = "unsubscribe",
  Bundle = "bundle",
  Passthrough = "passthrough",
  Block = "block",
}

export const serviceDomainName = "thxnothx.com";
export const shortServiceDomainName = "thx...";
export const serviceEmailSuffix = "@" + serviceDomainName;
export const teamEmailAddress = "team" + serviceEmailSuffix;
export const unsubscribeEmailAddress = "unsubscribe" + serviceEmailSuffix;
export const convertEmailAddress = "convert" + serviceEmailSuffix;
export const bundleEmailAddress = "bundle" + serviceEmailSuffix;
export const noReplyEmailAddress = "no-reply" + serviceEmailSuffix;

interface UrlSet {
  prod: string;
  local: string;
}

export type UrlEnvironment = keyof UrlSet;

export const LambdaApiBaseUrl = {
  prod: "https://api.thxnothx.com",
  local: "http://localhost:3000",
};

export const NodeApiBaseUrl = {
  prod: "https://kbay8nmqrq.us-east-1.awsapprunner.com",
  local: "http://localhost:7080",
};

export const WebBaseUrl = {
  prod: "https://www.thxnothx.com",
  local: "http://localhost:3000",
};

export type WebEnv = keyof typeof WebBaseUrl;

export const UserContentBaseUrl = {
  prod: "https://qjxhbmnhtu.us-east-1.awsapprunner.com",
  local: "http://localhost:5080",
};

export const TntBaseUrl: UrlSet = {
  prod: "https://app.thxnothx.com",
  local: "http://localhost:6080",
};

export const TntUserBaseUrl: UrlSet = {
  prod: "https://user.thxnothx.com",
  local: "http://local-user:6080",
};

const addHashIfNeeded = (hashStr: string) => (hashStr ? "#" + hashStr : "");

export const TntAllUrls = {
  // These URLs do not require a user
  Faq: (
    id:
      | ""
      | "junkvsspam"
      | "canspam"
      | "whycharge"
      | "idoitmyself"
      | "rumagic"
      | "marketersheart"
      | "wontmissimportant"
      | "customdomain"
      | "passwords"
      | "bundlevsforward" = "",
  ) => {
    return `/content/faq` + addHashIfNeeded(id);
  },
  HowItWorks: (id: "" = "") => `/content/howitworks` + addHashIfNeeded(id),
  HowPerSenderWorks: () => `/content/persender`,
  NewsletterLandingPage: (id: "" | "signup" = "") => `/content/newsletters` + addHashIfNeeded(id),
  Root: () => `/`,
  SignUp: () => `/#signup`,
  // use base url for login link since the user
  // may be on www and cookies are only set on app.thxnothx.com
  Login: () => `/login`,
  Logout: () => `/logout`, // This should never be used for a GET request
  // this is the endpoint for a user to unsubscribe from an email coming from TnT
  Unsubscribe: (unsubscribeToken: string) =>
    `/account/unsubscribe?${unsubscribeTokenParameterName}=${unsubscribeToken}`,

  VerifyUser: () => `/verify`,

  ////////// These URLs require a user ////////
  User: {
    Read: () => `/u/read`,
    SampleRead: () => `/sampleread`,
    AutofillHelp: () => `/u/autofill`,
  },

  Inbox: () => {
    return `/u/inbox`;
  },
  Read: () => {
    return `/u/read`;
  },
  AllEmails: () => {
    return `/u/inbox?seeAll=true`;
  },
  IndividualEmail: (emailId: Id) => {
    return `/u/emails/${emailId}`;
  },
  IndividualEmailAction: (
    emailId: Id,
    action: EmailAction,
    actionToken: Token /* this is just a regular token for the user */,
  ) => {
    // note that this action will *only* work if there's a token attached
    return `/a/emails/${emailId}?action=${action}&${ACTION_TOKEN_PARAM_NAME}=${actionToken}`;
  },
  Sender: (sendSourceId: Id) => {
    return `/u/senders/${sendSourceId}`;
  },
  AccountSettings: (id: "" | "username" | "sendFrequency" | "emailBundling" = "") =>
    `/u/user/settings` + addHashIfNeeded(id),
  Admin: {
    ViewS3Email: (env: UrlEnvironment, s3Key: string) =>
      `${TntBaseUrl[env]}/admin/viewS3Email/${s3Key}`,
  },
};

export const TntUser = {
  IndividualEmailHtmlUNSAFE: (
    env: UrlEnvironment,
    emailId: string,
    userToken: UserToken,
    showImg: boolean,
  ): string =>
    `${TntUserBaseUrl[env]}/u/email-html/${encodeURIComponent(
      emailId,
    )}?userToken=${userToken}&showImg=${showImg}`,
  DownloadAttachment: (env: UrlEnvironment, emailId: string, idx: number, userToken: UserToken) =>
    `${TntUserBaseUrl[env]}/u/email-attach/${encodeURIComponent(
      emailId,
    )}/${idx}?userToken=${userToken}`,
};

export function ForEmailPublicTnt(relativeUrl: string) {
  if (relativeUrl.startsWith("http")) {
    return relativeUrl;
  }

  return TntBaseUrl["prod"] + relativeUrl;
}

export const getUserThxNoThxAddress = (username: string) => username + serviceEmailSuffix;
export const getUserPerSenderAddressSuffix = (username: string) =>
  `@${username}.${serviceDomainName}`;
export const getUserPerSenderAddressSuffixShortened = (username: string) =>
  `@${username}.${shortServiceDomainName}`;
